import { roboqlStringLiteral } from "@/shared/domain/query/roboqlLiteral";
import { roboqlLinkTo } from "@/shared/domain/query/roboqlSearchParam";
import {
  CollectionTab,
  DatasetTab,
  Goto,
  SessionTab,
} from "@/shared/navigation";

import {
  AgentThreadGoalRecord,
  AgentThreadToolDetailResponse,
} from "./AgentThreadRecord";

/**
 * Goal-type discriminators that ship with a typed goal payload. Kept as string
 * literals (not an enum) to mirror the wire format exactly; the backend's
 * ``GoalType`` is the source of truth.
 */
const GOAL_TYPE_DATASET_SUMMARY = "dataset_summary";
const GOAL_TYPE_DATASET_TRIAGE = "dataset_triage";
const GOAL_TYPE_CREATE_EVENTS = "create_events";
const GOAL_TYPE_SESSION_CREATE_EVENTS = "session_create_events";
const GOAL_TYPE_SESSION_SUMMARY = "session_summary";
const GOAL_TYPE_SESSION_TRIAGE = "session_triage";

/**
 * Triage goal types. They share one achieve-input shape (``label_decisions``)
 * and one declaration shape (``label_vocabulary``), so they share the params
 * extraction and the combined vocabulary + applied-labels view.
 */
const TRIAGE_GOAL_TYPES: ReadonlySet<string> = new Set([
  GOAL_TYPE_DATASET_TRIAGE,
  GOAL_TYPE_SESSION_TRIAGE,
]);

/**
 * Create-events goal types. They share one declaration shape
 * (``event_vocabulary``) and one achieve-tool result, so they share the params
 * extraction and the events outcome parser.
 */
const CREATE_EVENTS_GOAL_TYPES: ReadonlySet<string> = new Set([
  GOAL_TYPE_CREATE_EVENTS,
  GOAL_TYPE_SESSION_CREATE_EVENTS,
]);

/**
 * Parameters declared on a goal — extracted from ``goal_data`` (the goal's
 * declaration), so they're available without any extra fetch. Only the fields
 * the hover card renders are surfaced; absent fields mean "not applicable to
 * this goal type" or "not provided".
 */
interface GoalParams {
  /** Triage: the candidate labels the agent deliberates over. */
  labelOptions?: string[];
  /** Create-events: the event types the agent may create. */
  eventTypes?: string[];
  /** Create-events: the collection created events are filed into, if any. */
  collectionId?: string;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

function keysOf(value: unknown): string[] | undefined {
  const record = asRecord(value);
  return record && Object.keys(record);
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * Pull the renderable declaration parameters out of a goal's ``goal_data``.
 * Returns an empty object for goal types (or shapes) with nothing to show
 * (e.g. dataset_summary, which declares no caller-facing parameters).
 */
export function paramsForGoal(goal: AgentThreadGoalRecord): GoalParams {
  const data = goal.goal_data ?? {};
  if (TRIAGE_GOAL_TYPES.has(goal.goal_type)) {
    return { labelOptions: keysOf(data.label_vocabulary) };
  }
  if (CREATE_EVENTS_GOAL_TYPES.has(goal.goal_type)) {
    return {
      eventTypes: keysOf(data.event_vocabulary),
      collectionId: stringOrUndefined(data.collection_id),
    };
  }
  return {};
}

/** Triage outcome: the labels the agent actually applied as tags. */
interface TriageOutcome {
  kind: "triage";
  appliedLabels: string[];
}

/** Summary outcome: the summary text the agent persisted. */
interface SummaryOutcome {
  kind: "summary";
  summary: string;
}

/** Create-events outcome: the ids of the events the agent created. */
export interface EventsOutcome {
  kind: "events";
  createdEventIds: string[];
  /**
   * The event types the agent used, in first-use order. A present key means
   * the type was used; the value is how many events of that type were created,
   * or ``undefined`` when the per-type counts can't be trusted (the input
   * listed a different number of events than were created — a partial
   * success). Empty when nothing was created.
   */
  createdByType: Map<string, number | undefined>;
  /** Where to browse the created events; absent when nothing identifies one. */
  target?: EventsTarget;
}

/**
 * The page that lists a create-events goal's created events. A collection is
 * preferred because it can hold events from any scope; otherwise the dataset
 * or session the events were created on.
 */
interface EventsTarget {
  kind: "collection" | "dataset" | "session";
  id: string;
}

export type GoalOutcome = TriageOutcome | SummaryOutcome | EventsOutcome;

const EVENT_ID_PATTERN = /\bevt?_[a-z0-9]+\b/g;

interface CreateEventsResult {
  createdEventIds: string[];
  /** Only present in the structured response. */
  collectionId?: string;
  collectionFilingFailed: boolean;
}

/** Read one candidate result: the structured response object, or narration
 *  text from which the event ids are extracted in order, de-duplicated. */
function readCreateEventsCandidate(
  candidate: unknown,
): CreateEventsResult | undefined {
  if (typeof candidate === "string") {
    const ids = Array.from(new Set(candidate.match(EVENT_ID_PATTERN) ?? []));
    return ids.length > 0
      ? { createdEventIds: ids, collectionFilingFailed: false }
      : undefined;
  }
  const record = asRecord(candidate);
  if (!Array.isArray(record?.created_event_ids)) {
    return undefined;
  }
  return {
    createdEventIds: record.created_event_ids.filter(
      (id): id is string => typeof id === "string",
    ),
    collectionId: stringOrUndefined(record.collection_id),
    collectionFilingFailed: record.collection_filing_failed === true,
  };
}

/**
 * Read a create-events achieve-tool result. The backend's current structured
 * response (``CreateEventsAchieveResponse``) and older plain-text narration
 * both appear, in ``tool_result.payload`` and, on the legacy envelope, in
 * ``raw_response.toolResult.content[].json`` / ``.text``. Sources are tried in
 * that order and the first that yields a result wins.
 */
function readCreateEventsResult(
  detail: AgentThreadToolDetailResponse,
): CreateEventsResult | undefined {
  const content = asRecord(
    detail.tool_result?.raw_response?.toolResult,
  )?.content;
  const blocks = Array.isArray(content) ? content.map(asRecord) : [];
  const candidates = [
    detail.tool_result?.payload,
    ...blocks.map((block) => block?.json),
    ...blocks.map((block) => block?.text),
  ];
  for (const candidate of candidates) {
    const result = readCreateEventsCandidate(candidate);
    if (result) {
      return result;
    }
  }
  return undefined;
}

/** Triage outcome from the achieve-tool *input*: the labels the agent applied. */
function parseTriageOutcome(
  detail: AgentThreadToolDetailResponse,
): TriageOutcome {
  const decisions = (detail.tool_use?.input ?? {}).label_decisions;
  if (!Array.isArray(decisions)) {
    // A missing ``label_decisions`` is a data anomaly, but it is reported the
    // same as "deliberated and applied nothing": an empty applied set.
    return { kind: "triage", appliedLabels: [] };
  }
  const appliedLabels = decisions
    .map(asRecord)
    .filter(
      (decision): decision is Record<string, unknown> =>
        decision !== undefined && decision.applies === true,
    )
    .map((decision) => decision.label)
    .filter((label): label is string => typeof label === "string");
  return { kind: "triage", appliedLabels };
}

/** Summary outcome from the achieve-tool *input*: the summary text the agent wrote. */
function parseSummaryOutcome(
  detail: AgentThreadToolDetailResponse,
): SummaryOutcome {
  const summary = (detail.tool_use?.input ?? {}).summary;
  return {
    kind: "summary",
    summary: typeof summary === "string" ? summary : "",
  };
}

/** Event names in the achieve-tool *input*, counted per type; non-string names
 *  are ignored. */
function countInputEventsByType(
  detail: AgentThreadToolDetailResponse,
): Map<string, number> {
  const events = (detail.tool_use?.input ?? {}).events;
  const counts = new Map<string, number>();
  if (!Array.isArray(events)) {
    return counts;
  }
  for (const event of events) {
    const name = asRecord(event)?.name;
    if (typeof name === "string") {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * Create-events outcome. The created ids come from the achieve-tool *result*
 * (what actually got created); the per-type breakdown comes from the *input*
 * (the result doesn't name types), so it is only trusted when the input listed
 * exactly as many events as were created.
 */
function parseEventsOutcome(
  detail: AgentThreadToolDetailResponse,
  goal: AgentThreadGoalRecord,
): EventsOutcome {
  const result = readCreateEventsResult(detail);
  const datasetId = stringOrUndefined(goal.goal_data?.dataset_id);
  const sessionId = stringOrUndefined(goal.goal_data?.session_id);
  let target: EventsTarget | undefined;
  if (result?.collectionId && !result.collectionFilingFailed) {
    target = { kind: "collection", id: result.collectionId };
  } else if (datasetId) {
    target = { kind: "dataset", id: datasetId };
  } else if (sessionId) {
    target = { kind: "session", id: sessionId };
  }
  const createdEventIds = result?.createdEventIds ?? [];
  const inputCounts = countInputEventsByType(detail);
  const inputTotal = Array.from(inputCounts.values()).reduce(
    (sum, n) => sum + n,
    0,
  );
  const countsReliable = inputTotal === createdEventIds.length;
  const createdByType = new Map<string, number | undefined>();
  if (createdEventIds.length > 0) {
    for (const [name, count] of inputCounts) {
      createdByType.set(name, countsReliable ? count : undefined);
    }
  }
  return {
    kind: "events",
    createdEventIds,
    createdByType,
    target,
  };
}

/**
 * The most created events a goal's link filters to by id. Each 25-character id
 * adds about 50 characters to the encoded URL, so 50 ids keep the link under
 * 2,500 characters, well inside what browsers and servers carry.
 */
export const MAX_LINKED_EVENT_IDS = 50;

/**
 * The link to a create-events goal's created events: the session's Events tab
 * or the collection's Items tab, filtered to exactly the created events, or
 * the dataset's Events tab unfiltered, since that tab does not read a RoboQL
 * filter from its URL. `undefined` when the outcome names no target.
 *
 * The filter is left off, so the link opens the whole container, when there
 * are no created ids or more than {@link MAX_LINKED_EVENT_IDS}. RoboQL has no
 * `IN`, so the filter is an `OR` of `event_id` equalities.
 */
export function createdEventsLink(outcome: EventsOutcome): string | undefined {
  const { target, createdEventIds } = outcome;
  if (target === undefined) {
    return undefined;
  }
  let pathname: string;
  switch (target.kind) {
    case "collection":
      pathname = Goto.createCollectionRoute(target.id, CollectionTab.Items);
      break;
    case "session":
      pathname = Goto.createSessionRoute(target.id, SessionTab.Events);
      break;
    case "dataset":
      return Goto.createDatasetTabRoute(target.id, DatasetTab.Events);
  }
  if (
    createdEventIds.length === 0 ||
    createdEventIds.length > MAX_LINKED_EVENT_IDS
  ) {
    return pathname;
  }
  const query = createdEventIds
    .map((id) => `event_id = ${roboqlStringLiteral(id)}`)
    .join(" OR ");
  return roboqlLinkTo(pathname, query);
}

/**
 * Per-goal-type outcome parsers — the single registry that decides both
 * *whether* a goal type yields a renderable outcome (its presence as a key) and
 * *how* that outcome is derived (the parser). ``outcomeForGoal`` and
 * ``goalHasFetchableOutcome`` both read from here, so a new goal type is wired
 * up by adding one entry. Triage and summary parse the achieve-tool input (the
 * labels the LLM chose, the summary it wrote); create-events parses the
 * result (the ids of events actually created).
 *
 * A ``Map`` rather than an object so that a goal type such as ``"constructor"``
 * can't resolve to an inherited ``Object.prototype`` member.
 */
const OUTCOME_PARSERS = new Map<
  string,
  (
    detail: AgentThreadToolDetailResponse,
    goal: AgentThreadGoalRecord,
  ) => GoalOutcome
>([
  [GOAL_TYPE_DATASET_TRIAGE, parseTriageOutcome],
  [GOAL_TYPE_DATASET_SUMMARY, parseSummaryOutcome],
  [GOAL_TYPE_CREATE_EVENTS, parseEventsOutcome],
  [GOAL_TYPE_SESSION_CREATE_EVENTS, parseEventsOutcome],
  [GOAL_TYPE_SESSION_SUMMARY, parseSummaryOutcome],
  [GOAL_TYPE_SESSION_TRIAGE, parseTriageOutcome],
]);

/**
 * Derive a goal's outcome from the achieve-tool's fetched detail. Returns
 * ``null`` for goal types without a renderable outcome.
 */
export function outcomeForGoal(
  goal: AgentThreadGoalRecord,
  detail: AgentThreadToolDetailResponse,
): GoalOutcome | null {
  const parse = OUTCOME_PARSERS.get(goal.goal_type);
  return parse ? parse(detail, goal) : null;
}

/**
 * Whether a goal can render an outcome at all: its type has an outcome parser
 * and it has a recorded achieve-tool invocation (``achieve_tool_use_id``) to
 * fetch and parse. Pending and unhandleable goals have none.
 */
export function goalHasFetchableOutcome(goal: AgentThreadGoalRecord): boolean {
  return (
    typeof goal.achieve_tool_use_id === "string" &&
    goal.achieve_tool_use_id.length > 0 &&
    OUTCOME_PARSERS.has(goal.goal_type)
  );
}

type CombinedOutcomeView = "triage" | "events";

/**
 * Whether a goal type folds its declared vocabulary and outcome into one
 * highlighted chip list (triage's labels, create-events' event types) rather
 * than rendering the generic params + outcome rows. Returns which combined
 * view to use, or ``undefined`` for the generic rows.
 */
export function combinedOutcomeViewForGoal(
  goalType: string,
): CombinedOutcomeView | undefined {
  if (TRIAGE_GOAL_TYPES.has(goalType)) {
    return "triage";
  }
  if (CREATE_EVENTS_GOAL_TYPES.has(goalType)) {
    return "events";
  }
  return undefined;
}

/**
 * Whether opening a goal chip's card should kick off the lazy achieve-tool
 * fetch. True only on the first open of a fetchable chip: once a fetch is in
 * flight (``isFetching``), has resolved (``data``), or has *failed* (``error``),
 * further opens are no-ops.
 *
 * The ``error`` guard is load-bearing. Without it, a permanent failure (404,
 * 403, malformed response) leaves ``data`` undefined and ``isFetching`` false —
 * indistinguishable from the pre-fetch state — so every subsequent hover would
 * re-issue the request in an unbounded retry loop.
 */
export function shouldFetchToolDetail(state: {
  toolUseId: string | undefined;
  data: unknown;
  isFetching: boolean;
  error: unknown;
}): boolean {
  const { toolUseId, data, isFetching, error } = state;
  return Boolean(toolUseId) && !data && !isFetching && !error;
}
